// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

const renderStatusIcon = (status) => {
  const x = (status || '').toLowerCase();
  if (x === 'ok' || x === 'success') return <CheckCircleRoundedIcon />;
  if (x === 'warning') return <WarningRoundedIcon />;
  if (x === 'error') return <ErrorRoundedIcon />;
  return <HelpOutlineRoundedIcon />;
};

const fmtNumber = (v) =>
  typeof v === 'number'
    ? new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(v)
    : '-';

const fmtDecimal = (v, digits = 1) =>
  typeof v === 'number'
    ? new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(v)
    : '-';

const fmtPercent = (v, digits = 1) =>
  typeof v === 'number' ? `${fmtDecimal(v, digits)}%` : '-';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('da-DK') : '-');

const statusColor = (s) => {
  switch ((s || '').toLowerCase()) {
    case 'ok':
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

// const statusIcon = (s) => {
//   if (s === 'Success') return CheckCircleRoundedIcon;
//   if (s === 'Warning') return WarningRoundedIcon;
//   if (s === 'Error') return ErrorRoundedIcon;
//   if (s === 'High') return PriorityHighRoundedIcon;
//   return HelpOutlineRoundedIcon;
// };

function StatCard({ title, value, subtitle, tooltip, highlight, children }) {
  const content = (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        backgroundColor: (t) =>
          highlight ? t.palette.action.hover : 'background.paper',
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6">{value}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {children}
      </Stack>
    </Paper>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}

function ProgressPercent({ value }) {
  if (typeof value !== 'number') return null;
  return (
    <Box sx={{ mt: 1 }}>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, value))}
        sx={{
          height: 8,
          borderRadius: 999,
        }}
      />
    </Box>
  );
}

export default function BuildingEnergySavingsDialog({
  open,
  onClose,
  energySavings,
  energySavingsPreviousYears,
}) {
  const { t } = useTranslation();
  const current = energySavings?.Data?.EnergySavingsSummary || null;
  const baseUnit = current?.BaseUnit || 'kWh';

  // pick the most recent previous entry (if multiple keys are present)
  const { prevLabel, prev } = useMemo(() => {
    if (!energySavingsPreviousYears) return { prevLabel: null, prev: null };
    const entries = Object.entries(energySavingsPreviousYears || {});
    if (!entries.length) return { prevLabel: null, prev: null };

    // Sort by EndTime if present; otherwise keep first
    const normalized = entries
      .map(([label, obj]) => ({
        label,
        summary: obj?.Data?.EnergySavingsSummary || null,
        end: obj?.Data?.EndTime ? new Date(obj.Data.EndTime).getTime() : 0,
      }))
      .filter((x) => x.summary);
    if (!normalized.length) return { prevLabel: null, prev: null };
    normalized.sort((a, b) => b.end - a.end);
    return { prevLabel: normalized[0].label, prev: normalized[0].summary };
  }, [energySavingsPreviousYears]);

  const pct = current?.TotalEnergySavingsPercentage ?? null;
  const savings = current?.TotalEnergySavings ?? null;
  const usage = current?.TotalEnergyUsage ?? null;
  const estWithout =
    current?.TotalEstimatedEnergyUsageWithoutOptimization ?? null;
  const estWith = current?.TotalEstimatedEnergyUsageWithOptimization ?? null;

  const yoyPct =
    prev &&
    typeof pct === 'number' &&
    typeof prev.TotalEnergySavingsPercentage === 'number'
      ? pct - prev.TotalEnergySavingsPercentage
      : null;

  const yoySavings =
    prev &&
    typeof savings === 'number' &&
    typeof prev.TotalEnergySavings === 'number'
      ? savings - prev.TotalEnergySavings
      : null;

  const status = energySavings?.Status;
  const warnings = energySavings?.Warnings || [];
  const errors = energySavings?.Errors || [];
  const startTime = energySavings?.Data?.StartTime;
  const endTime = energySavings?.Data?.EndTime;
  const updateTime = energySavings?.UpdateTime;

  const iconColorKey = statusColor(status);
  const StatusIcon = renderStatusIcon(status);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 2 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {StatusIcon &&
            (StatusIcon.type ? (
              <StatusIcon.type
                sx={{
                  ...(StatusIcon.props?.sx || {}),
                  fontSize: 22,
                  color: (theme) =>
                    iconColorKey === 'default'
                      ? theme.palette.text.disabled
                      : theme.palette[iconColorKey].main,
                }}
              />
            ) : null)}
          <Typography variant="h6" component="span">
            {t('glossary.energySavings', { ns: 'buildingOptimization' })}
          </Typography>
          <Box flex={1} />
          <Typography variant="caption" color="text.secondary">
            {t('energySavingsDialog.updated', { ns: 'buildingOptimization' })}:{' '}
            {fmtDate(updateTime)}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Period */}
        <Box mb={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              icon={<InfoOutlinedIcon fontSize="small" />}
              label={`Periode: ${fmtDate(startTime)} -> ${fmtDate(endTime)}`}
              variant="outlined"
            />
            {prevLabel && (
              <Chip
                size="small"
                variant="outlined"
                label={`Sammenlignes med: ${prevLabel}`}
              />
            )}
          </Stack>
        </Box>

        {/* Summary stats */}
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <StatCard
              title={t('buildingOptimization:tooltips.energySavingsPercentage')}
              value={fmtPercent(pct)}
              subtitle={
                yoyPct != null
                  ? (yoyPct >= 0 ? '↑ ' : '↓ ') + fmtPercent(Math.abs(yoyPct))
                  : undefined
              }
              tooltip="Procentuel besparelse i perioden"
              highlight
            >
              <ProgressPercent value={typeof pct === 'number' ? pct : 0} />
            </StatCard>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <StatCard
              title={`Energibesparelse (${baseUnit})`}
              value={`${fmtNumber(savings)} ${baseUnit}`}
              subtitle={
                yoySavings != null
                  ? (yoySavings >= 0 ? '↑ ' : '↓ ') +
                    `${fmtNumber(Math.abs(yoySavings))} ${baseUnit}`
                  : undefined
              }
              tooltip="Akkumuleret besparelse i energienheder"
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <StatCard
              title={`Forbrug m/ optimering (${baseUnit})`}
              value={`${fmtNumber(estWith)} ${baseUnit}`}
              subtitle={
                typeof estWithout === 'number' && typeof estWith === 'number'
                  ? `Uden optimering: ${fmtNumber(estWithout)} ${baseUnit}`
                  : undefined
              }
              tooltip="Estimeret forbrug med og uden optimering"
            />
          </Grid>

          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t('glossary.consumptionOverview', {
                  ns: 'buildingOptimization',
                })}
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {t('glossary.actualConsumption', {
                        ns: 'buildingOptimization',
                      })}
                    </Typography>
                    <Typography variant="body1">
                      {fmtNumber(usage)} {baseUnit}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {t('glossary.estimatedWithoutOptimization', {
                        ns: 'buildingOptimization',
                      })}
                    </Typography>
                    <Typography variant="body1">
                      {fmtNumber(estWithout)} {baseUnit}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {t('glossary.estimatedWithOptimization', {
                        ns: 'buildingOptimization',
                      })}
                    </Typography>
                    <Typography variant="body1">
                      {fmtNumber(estWith)} {baseUnit}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Divider */}
        {(warnings.length > 0 || errors.length > 0) && (
          <Box my={2}>
            <Divider />
          </Box>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <Alert severity="error" icon={<ErrorOutlineRoundedIcon />}>
            <List dense disablePadding>
              {errors.map((e, i) => (
                <ListItem key={`err-${i}`} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={e.message || e.error_type || 'Ukendt fejl'}
                    secondary={[
                      e.error_type,
                      e.billing_meter_id && `måler #${e.billing_meter_id}`,
                      e.building_id && `bygning #${e.building_id}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <Alert severity="warning" icon={<WarningAmberRoundedIcon />}>
            <List dense disablePadding>
              {warnings.map((w, i) => (
                <ListItem key={`warn-${i}`} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={w.message || w.warning_type || 'Ukendt advarsel'}
                    secondary={[
                      w.warning_type,
                      w.billing_meter_id && `måler #${w.billing_meter_id}`,
                      w.building_id && `bygning #${w.building_id}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">
          {t('actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
